import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const RELATIONSHIPS = ["Self", "Spouse", "Parent", "Child", "Employee", "Partner", "Other"];
const EMPTY = { name: "", relationship: "Self", mobile: "", email: "", cards_assigned: [], spending_limit: "", allowed_categories: [], business_personal: "Business", remarks: "" };

export default function CardHolders() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = () => api.get("/card-holders").then((r) => setRows(r.data));
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        load();
    }, []);

    const del = async (id, name) => {
        if (!window.confirm(`Delete card holder "${name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/card-holders/${id}`);
            toast.success("Card holder deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const toggle = (k, val) => setF((s) => ({ ...s, [k]: s[k].includes(val) ? s[k].filter((x) => x !== val) : [...s[k], val] }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...f, spending_limit: f.spending_limit === "" ? null : Number(f.spending_limit) };
            if (editingId) {
                await api.patch(`/card-holders/${editingId}`, payload);
                toast.success("Card holder updated");
            } else {
                await api.post("/card-holders", payload);
                toast.success("Card holder saved");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            name: row.name || "",
            relationship: row.relationship || "Self",
            mobile: row.mobile || "",
            email: row.email || "",
            cards_assigned: row.cards_assigned || [],
            spending_limit: row.spending_limit ?? "",
            allowed_categories: row.allowed_categories || [],
            business_personal: row.business_personal || "Business",
            remarks: row.remarks || "",
        });
        setOpen(true);
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    return (
        <div data-testid="card-holders-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Card Holders</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Who uses what.</h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} holders across family and team.</p>
                </div>
                {hasRole("admin", "finance") && (
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-holder-btn" onClick={openAdd}><Plus size={16} className="mr-1.5" /> Add holder</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit card holder" : "New card holder"}</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="eyebrow">Name</Label><Input required value={f.name} onChange={setInput("name")} className="mt-1.5" /></div>
                                    <div>
                                        <Label className="eyebrow">Relationship</Label>
                                        <Select value={f.relationship} onValueChange={set("relationship")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Mobile</Label><Input value={f.mobile} onChange={setInput("mobile")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Email</Label><Input type="email" value={f.email} onChange={setInput("email")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Monthly spending limit</Label><Input type="number" value={f.spending_limit} onChange={setInput("spending_limit")} className="mt-1.5" placeholder="Optional" /></div>
                                    <div>
                                        <Label className="eyebrow">Usage</Label>
                                        <Select value={f.business_personal} onValueChange={set("business_personal")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent><SelectItem value="Business">Business</SelectItem><SelectItem value="Personal">Personal</SelectItem></SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label className="eyebrow">Cards assigned</Label>
                                    <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-stone-200 rounded-md p-3">
                                        {cards.map((c) => (
                                            <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer">
                                                <Checkbox checked={f.cards_assigned.includes(c.id)} onCheckedChange={() => toggle("cards_assigned", c.id)} />
                                                {c.bank_name} · •••• {c.last_4}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <Label className="eyebrow">Allowed expense categories</Label>
                                    <div className="mt-2 grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-stone-200 rounded-md p-3">
                                        {CATEGORIES.map((c) => (
                                            <label key={c} className="flex items-center gap-2 text-xs cursor-pointer">
                                                <Checkbox checked={f.allowed_categories.includes(c)} onCheckedChange={() => toggle("allowed_categories", c)} />
                                                {c}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" /></div>
                                <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">{editingId ? "Save changes" : "Save holder"}</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Name</th><th>Relationship</th><th>Mobile</th><th>Email</th><th className="text-right">Limit</th><th>Cards</th><th>Usage</th><th></th></tr>
                        </thead>
                        <tbody>
                            {rows.map((h) => (
                                <tr key={h.id}>
                                    <td className="font-medium">{h.name}</td>
                                    <td>{h.relationship}</td>
                                    <td>{h.mobile || "—"}</td>
                                    <td>{h.email || "—"}</td>
                                    <td className="text-right tabular-nums">{h.spending_limit != null ? fmtMoney(h.spending_limit) : "—"}</td>
                                    <td className="text-xs">
                                        {h.cards_assigned?.length ? h.cards_assigned.map((id) => cardMap[id]).filter(Boolean).map((c) => `•••• ${c.last_4}`).join(", ") : "—"}
                                    </td>
                                    <td>{h.business_personal}</td>
                                    <td className="text-right">
                                        {hasRole("admin", "finance") && (
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    onClick={() => openEdit(h)}
                                                    title="Edit holder"
                                                    className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                    data-testid={`holder-edit-${h.id}`}
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    onClick={() => del(h.id, h.name)}
                                                    title="Delete holder"
                                                    className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600"
                                                    data-testid={`holder-delete-${h.id}`}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-500 py-14">No card holders yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
